/**
 * Lo que le importa a un logístico al entrar: qué bloquea y qué va tarde.
 *
 * Todo sale de las MISMAS cargas que ya pide la pantalla de cargas de salida y
 * de los mismos criterios (`filaEnAlarma`, `estadoDe`). No hay un endpoint de
 * portada ni una segunda definición de «va tarde»: si la hubiera, el inicio y
 * la pantalla dirían cosas distintas de la misma carga y nadie sabría cuál
 * creer. Es la lección que ya dejó escrita `pickingPct` sobre medir el avance
 * en dos sitios distintos.
 *
 * Las dos listas NO se solapan a propósito, y el orden de evaluación es el
 * mismo que el del semáforo: una carga en alarma sale sólo en «va tarde»,
 * aunque también esté reservada sin empezar. Repetirla en las dos haría contar
 * dos veces el mismo trabajo.
 */
import type { CargaSalida } from '@/types'
import {
  fechaDe, filaEnAlarma, estadoDe, pesoExpedicionMandante,
} from '@/utils/cargasSalida'

/**
 * El día de expedición: lo que hay que sacar y si está listo.
 *
 * `palets` y `peso` cuentan los huecos en vez de sumarlos como ceros, y los
 * declaran en `sinDato`. Es la misma disciplina que `totalesCargas`: un total
 * al que le faltan tres cargas parece completo y nadie lo pone en duda, y con
 * esa cifra se decide si cabe en un camión.
 */
export interface ResumenDia {
  cargas: number
  /** La cifra ÚNICA de palets: real si la hay, y si no el estimado MONO, que
   *  es el conservador. La suma Muelles para saber qué cabe en un camión. */
  palets: number
  paletsSinDato: number
  /** Bruto + palet: lo que pesa el ENVÍO, no la mercancía. */
  peso: number
  pesoSinDato: number
  clientes: number
  /** Reparto por estado de preparación, para la barra. Suman `cargas`. */
  listas: number
  empezadas: number
  sinEmpezar: number
}

/** Una banda del calendario corto: un día concreto, o el cajón de «más adelante». */
export interface BandaDia {
  /** ISO `yyyy-mm-dd`, o `null` para el cajón final. */
  fecha: string | null
  cargas: number
  listas: number
  palets: number
}

export interface ResumenLogistica {
  /** Cargas vivas en la ventana que sirve el backend. */
  vivas: number
  /** Salen hoy, estén como estén. */
  salenHoy: number
  /** Picking al 100 %: nada que preparar, listas para subir al camión. */
  listas: number
  /** Lo que sale HOY, con detalle: es la pregunta de la mañana. */
  hoy: ResumenDia
  /** Los días siguientes, para ver venir la semana. */
  proximos: BandaDia[]
  /**
   * «Nada lo impide y va tarde»: reserva completa, picking sin terminar y
   * fecha de salida hoy o anterior. Es el trabajo más urgente que existe,
   * porque el material está atado y aun así no ha salido.
   */
  vanTarde: CargaSalida[]
  /**
   * Hay stock atado y nadie ha empezado. No van tarde todavía: es lo que se
   * puede adelantar hoy sin depender de nadie.
   */
  sinEmpezar: CargaSalida[]
}

/** Ordena por fecha de salida, las más antiguas primero. Sin fecha, al final:
 *  no se puede decir que algo sin fecha sea más urgente que algo con ella. */
function porFecha(a: CargaSalida, b: CargaSalida): number {
  const fa = a.fechaEnvioPrevista
  const fb = b.fechaEnvioPrevista
  if (!fa && !fb) return a.carga.localeCompare(b.carga)
  if (!fa) return 1
  if (!fb) return -1
  return fa.localeCompare(fb)
}

function esHoy(c: CargaSalida, hoy: Date): boolean {
  const f = fechaDe(c.fechaEnvioPrevista)
  if (!f) return false
  const h = new Date(hoy)
  h.setHours(0, 0, 0, 0)
  f.setHours(0, 0, 0, 0)
  return f.getTime() === h.getTime()
}

/** `yyyy-mm-dd` de una fecha local, sin pasar por UTC (que la movería un día). */
function claveDia(d: Date): string {
  const p = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`
}

/** ¿Está lista para salir? «Preparado», o ya con marca manual de transporte
 *  (que sólo existe sobre una carga preparada). */
function estaLista(c: CargaSalida): boolean {
  const e = estadoDe(c)
  return e === 'preparado' || e === 'transporte' || e === 'transporte_confirmado'
}

/** El resumen de un conjunto de cargas: cuánto hay, cuánto pesa y si está listo. */
function resumirDia(cargas: readonly CargaSalida[]): ResumenDia {
  let palets = 0, paletsSinDato = 0, peso = 0, pesoSinDato = 0
  let listas = 0, empezadas = 0, sinEmpezar = 0
  const clientes = new Set<string>()

  for (const c of cargas) {
    if (c.palets == null) paletsSinDato += 1
    else palets += c.palets

    const p = pesoExpedicionMandante(c)
    if (p == null) pesoSinDato += 1
    else peso += p

    if (c.cliente) clientes.add(c.cliente)

    // Tres cubos y no nueve: aquí no hay cargadas (se filtran antes) y a la
    // barra sólo le importa cuánto del día está listo para salir, cuánto se
    // está preparando y cuánto no se ha tocado. «Transporte» y «transporte
    // confirmado» son marcas manuales sobre una carga YA preparada, así que
    // cuentan como listas; las reservas, parciales o totales, como sin tocar
    // porque en las dos no se ha empezado el picking.
    const estado = estadoDe(c)
    if (estaLista(c)) listas += 1
    else if (estado === 'preparacion_parcial' || estado === 'en_preparacion') empezadas += 1
    else sinEmpezar += 1
  }

  return {
    cargas: cargas.length,
    palets, paletsSinDato,
    peso, pesoSinDato,
    clientes: clientes.size,
    listas, empezadas, sinEmpezar,
  }
}

/** Cuántos días por delante enseña el calendario corto, sin contar hoy. */
const DIAS_VISTA = 5

export function resumenLogistica(
  cargas: readonly CargaSalida[],
  hoy: Date = new Date(),
): ResumenLogistica {
  const vivas = cargas.filter(c => !c.expedida)

  const vanTarde = vivas.filter(c => filaEnAlarma(c, hoy)).sort(porFecha)
  // `reserva_total` es reserva física completa y CERO picking: en cuanto hay
  // picking el estado pasa a preparación. El `filaEnAlarma` de fuera
  // es el que evita que una carga salga en las dos listas.
  const sinEmpezar = vivas
    .filter(c => estadoDe(c) === 'reserva_total' && !filaEnAlarma(c, hoy))
    .sort(porFecha)

  const deHoy = vivas.filter(c => esHoy(c, hoy))

  // Los días siguientes, uno a uno, más un cajón final con todo lo que queda.
  // Los días vacíos SE PINTAN: un hueco en la semana es información —no hay
  // nada que sacar ese día— y saltárselo haría parecer que la semana es
  // continua cuando no lo es.
  const proximos: BandaDia[] = []
  const yaContadas = new Set<CargaSalida>(deHoy)
  for (let i = 1; i <= DIAS_VISTA; i++) {
    const d = new Date(hoy)
    d.setDate(d.getDate() + i)
    const clave = claveDia(d)
    const delDia = vivas.filter(c => c.fechaEnvioPrevista === clave)
    delDia.forEach(c => yaContadas.add(c))
    proximos.push({
      fecha: clave,
      cargas: delDia.length,
      listas: delDia.filter(c => estaLista(c)).length,
      palets: delDia.reduce((n, c) => n + (c.palets ?? 0), 0),
    })
  }
  // El cajón final recoge lo que no entra en la vista: lo de más adelante y
  // también lo ATRASADO y lo que no tiene fecha. Ninguna carga se pierde de la
  // cuenta, que es lo que haría que los números del inicio no cuadrasen con la
  // pantalla de cargas.
  const resto = vivas.filter(c => !yaContadas.has(c))
  proximos.push({
    fecha: null,
    cargas: resto.length,
    listas: resto.filter(c => estaLista(c)).length,
    palets: resto.reduce((n, c) => n + (c.palets ?? 0), 0),
  })

  return {
    vivas: vivas.length,
    salenHoy: deHoy.length,
    listas: vivas.filter(c => estaLista(c)).length,
    hoy: resumirDia(deHoy),
    proximos,
    vanTarde,
    sinEmpezar,
  }
}
